import { useEffect, useRef, useState, type RefObject } from 'react';
import { TYPING_INDICATOR_MS } from './documentCollaborationTypes';

export interface UseDocumentEditingSignalsOptions {
  enabled: boolean;
  iframeRef: RefObject<HTMLIFrameElement | null>;
  editingChangedType: string;
  typingActivityType: string;
  notifyEditing: (isEditing: boolean) => void | Promise<boolean>;
  notifyTyping: () => void;
  /** When true, suppress forwarding editing/typing (peer holds exclusive lock). */
  peerEditLocked?: boolean;
  onEditLockDeniedLocally?: () => void;
}

export interface DocumentEditingSignalsState {
  /** True while this client has recent typing/cell-input activity (before server roundtrip). */
  isLocalTyping: boolean;
}

/**
 * Forwards iframe editing/typing postMessages to the document collaboration hub and tracks
 * immediate local typing state for the typing-dots animation.
 */
export function useDocumentEditingSignals({
  enabled,
  iframeRef,
  editingChangedType,
  typingActivityType,
  notifyEditing,
  notifyTyping,
  peerEditLocked = false,
  onEditLockDeniedLocally
}: UseDocumentEditingSignalsOptions): DocumentEditingSignalsState {
  const notifyEditingRef = useRef(notifyEditing);
  const notifyTypingRef = useRef(notifyTyping);
  const peerEditLockedRef = useRef(peerEditLocked);
  const onDeniedRef = useRef(onEditLockDeniedLocally);
  const [localTypingUntilMs, setLocalTypingUntilMs] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());
  notifyEditingRef.current = notifyEditing;
  notifyTypingRef.current = notifyTyping;
  peerEditLockedRef.current = peerEditLocked;
  onDeniedRef.current = onEditLockDeniedLocally;

  useEffect(() => {
    if (!enabled) {
      setLocalTypingUntilMs(0);
      return;
    }

    function onMessage(event: MessageEvent) {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const data = event.data;
      if (!data || typeof data !== 'object' || !('type' in data)) return;

      if (data.type === editingChangedType && 'editing' in data) {
        const editing = Boolean(data.editing);
        if (editing && peerEditLockedRef.current) {
          onDeniedRef.current?.();
          return;
        }
        void Promise.resolve(notifyEditingRef.current(editing)).then((accepted) => {
          if (editing && accepted === false) onDeniedRef.current?.();
        });
        return;
      }
      if (data.type === typingActivityType) {
        if (peerEditLockedRef.current) {
          onDeniedRef.current?.();
          return;
        }
        setLocalTypingUntilMs(Date.now() + TYPING_INDICATOR_MS);
        notifyTypingRef.current();
      }
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [enabled, iframeRef, editingChangedType, typingActivityType]);

  useEffect(() => {
    if (!enabled || localTypingUntilMs <= Date.now()) return;

    const timer = window.setInterval(() => setNowMs(Date.now()), 200);
    return () => window.clearInterval(timer);
  }, [enabled, localTypingUntilMs]);

  return {
    isLocalTyping: enabled && localTypingUntilMs > nowMs
  };
}
